import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { ContextListDto } from '../../../overmind/actions/ai/ai-contexts';
import { Button, PageHeader, Table, TableColumn, TableRow, Modal, KebabMenu, formatDate } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { ConfirmationModalLayout } from '../../../widgets/layouts';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';

export function ContextsPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [contexts, setContexts] = useState<ContextListDto[]>();
  const [contextToDelete, setContextToDelete] = useState<ContextListDto>();

  useEffect(() => {
    async function getContexts() {
      actions.startLoader({ path: PagePath.aiManagementContexts, type: FetchType.PageFetching });
      const response = await actions.getContextsByCompany();
      setContexts(response);
      actions.stopLoader(PagePath.aiManagementContexts);
    }

    getContexts();
  }, [companyVariables.slug]);

  function handleCreateFreeText() {
    keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug!).replace(':contextId', 'freetext')}`);
  }

  function handleCreateWebSearch() {
    keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug!).replace(':contextId', 'websearch')}`);
  }

  function handleCreateFile() {
    keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug!).replace(':contextId', 'file')}`);
  }

  function handleEditContext(context: ContextListDto) {
    keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug!).replace(':contextId', context.id)}`);
  }

  function handleDeleteClick(context: ContextListDto) {
    setContextToDelete(context);
  }

  async function handleDeleteConfirm() {
    if (!contextToDelete) return;

    actions.startLoader({ path: PagePath.aiManagementContexts, type: FetchType.Sending });

    await actions.deleteContext(contextToDelete.id);
    setContextToDelete(undefined);

    const response = await actions.getContextsByCompany();
    setContexts(response);

    actions.stopLoader(PagePath.aiManagementContexts);
  }

  return (
    <PageLoaderLayer path={PagePath.aiManagementContexts}>
      <div className="wrapper stack">
        <PageHeader
          breadcrumbs={[
            { name: 'AI management', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Contexts', url: location.pathname },
          ]}
          title="Contexts"
          subtitle="Manage contexts for your AI agents"
          divider
        >
          <div style={{ display: 'flex', gap: 12 }}>
            <Button type="button" onClick={handleCreateFreeText}>
              New free text context
            </Button>

            <Button type="button" onClick={handleCreateWebSearch}>
              New web search context
            </Button>

            <Button type="button" onClick={handleCreateFile}>
              New file context
            </Button>
          </div>
        </PageHeader>

        <div style={{ marginTop: 24, width: '100%' }}>
          {!!contexts?.length ? (
            <Table standAloneConfigColumn>
              {contexts?.map(context => (
                <TableRow
                  key={context.id}
                  id={context.id}
                  configCell={() => (
                    <KebabMenu
                      items={[
                        {
                          label: 'Edit',
                          onClick: () => handleEditContext(context),
                        },
                        {
                          label: 'Delete',
                          onClick: () => handleDeleteClick(context),
                        },
                      ]}
                    />
                  )}
                >
                  <TableColumn id="label" label="Label" onClick={() => handleEditContext(context)}>
                    {context.label}
                  </TableColumn>

                  <TableColumn id="type" label="Type">
                    {context.type}
                  </TableColumn>

                  <TableColumn id="dateCreated" label="Date Created">
                    {formatDate(new Date(context.dateCreated))}
                  </TableColumn>
                </TableRow>
              ))}
            </Table>
          ) : (
            <EmptyState title="No contexts found" subtitle="Create a new context to get started" />
          )}
        </div>
      </div>

      <Modal open={!!contextToDelete} onClose={() => setContextToDelete(undefined)}>
        <ConfirmationModalLayout title="Delete Context" subtitle={`Are you sure you want to delete the context "${contextToDelete?.label}"?`} onClickCancel={() => setContextToDelete(undefined)} submitButtonText="Delete" onClickSubmit={handleDeleteConfirm} />
      </Modal>
    </PageLoaderLayer>
  );
}
